/**
 * @fileoverview Collapsible section for deep dive/opinion content generation
 */

import { useState } from 'react';
import {
  SparklesIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  PencilIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';

export default function DeepDiveSection({
  issueNumber,
  availableItems = [],
  deepDiveContent = null,
  onContentSave,
  onGenerate,
  isGenerating = false,
  generatedContent = null,
  error = null,
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [userCommentary, setUserCommentary] = useState('');
  const [evidenceGuidance, setEvidenceGuidance] = useState('');
  const [sectionType, setSectionType] = useState('opinion');
  const [editedParagraphs, setEditedParagraphs] = useState({});
  const [editingIndex, setEditingIndex] = useState(null);

  const handleToggleItem = (itemHash) => {
    setSelectedItems((prev) => {
      if (prev.includes(itemHash)) {
        return prev.filter((h) => h !== itemHash);
      }
      if (prev.length >= 5) {
        return prev; // Max 5 items
      }
      return [...prev, itemHash];
    });
  };

  const handleGenerate = () => {
    if (selectedItems.length === 0 || userCommentary.length < 10) {
      return;
    }
    onGenerate(selectedItems, userCommentary, evidenceGuidance, sectionType);
  };

  const handleEditParagraph = (index) => {
    setEditingIndex(index);
    if (!editedParagraphs[index] && generatedContent?.paragraphs?.[index]) {
      setEditedParagraphs((prev) => ({
        ...prev,
        [index]: generatedContent.paragraphs[index].content,
      }));
    }
  };

  const handleSaveEdit = (index) => {
    setEditingIndex(null);
  };

  const handleSaveContent = () => {
    if (!generatedContent) return;

    const content = {
      title: generatedContent.title,
      paragraphs: generatedContent.paragraphs.map((p, index) => ({
        ...p,
        content: editedParagraphs[index] || p.content,
      })),
      sectionType,
      generatedAt: new Date().toISOString(),
    };

    if (onContentSave) {
      onContentSave(content);
    }
  };

  const handleClear = () => {
    setSelectedItems([]);
    setUserCommentary('');
    setEvidenceGuidance('');
    setEditedParagraphs({});
    setEditingIndex(null);
  };

  const displayContent = generatedContent || deepDiveContent;

  return (
    <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800/50 overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors"
      >
        <div className="flex items-center gap-2">
          <SparklesIcon className="h-5 w-5 text-amber-600 dark:text-amber-400" />
          <span className="font-medium text-amber-900 dark:text-amber-200">Deep Dive / Opinion Section</span>
          {deepDiveContent && (
            <span className="px-2 py-0.5 text-xs bg-amber-200 dark:bg-amber-900/50 text-amber-800 dark:text-amber-200 rounded">
              Content saved
            </span>
          )}
        </div>
        {isExpanded ? (
          <ChevronUpIcon className="h-5 w-5 text-amber-500 dark:text-amber-400" />
        ) : (
          <ChevronDownIcon className="h-5 w-5 text-amber-500 dark:text-amber-400" />
        )}
      </button>

      {isExpanded && (
        <div className="px-4 pb-4 space-y-4">
          <p className="text-xs text-amber-700 dark:text-amber-300">
            Create a deep dive or opinion piece by selecting reference articles, adding your commentary, and generating 3-5 paragraphs of content.
          </p>

          {/* Error State */}
          {error && (
            <div className="rounded-md bg-red-50 dark:bg-red-900/30 p-3">
              <p className="text-sm text-red-700 dark:text-red-200">{error}</p>
            </div>
          )}

          {/* Generation Form - Show when no generated content */}
          {!displayContent && (
            <div className="space-y-4">
              {/* Section Type */}
              <div>
                <label className="block text-xs font-medium text-amber-700 dark:text-amber-300 mb-1">
                  Section Type
                </label>
                <select
                  value={sectionType}
                  onChange={(e) => setSectionType(e.target.value)}
                  disabled={isGenerating}
                  className="w-full px-3 py-2 text-sm border border-amber-200 dark:border-amber-800/50 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                >
                  <option value="opinion">Opinion Piece</option>
                  <option value="deep-dive">Deep Dive Analysis</option>
                </select>
              </div>

              {/* Item selection */}
              <div>
                <label className="block text-xs font-medium text-amber-700 dark:text-amber-300 mb-1">
                  Reference Articles ({selectedItems.length}/5 selected)
                </label>
                <div className="max-h-36 overflow-y-auto space-y-1 bg-white dark:bg-gray-800 rounded-lg border border-amber-100 dark:border-amber-900/40 p-2">
                  {availableItems.length === 0 ? (
                    <p className="text-xs text-gray-500 dark:text-gray-400 text-center py-4">
                      No items available. Add items to the issue first.
                    </p>
                  ) : (
                    availableItems.map((item) => {
                      const isSelected = selectedItems.includes(item.hash);

                      return (
                        <label
                          key={item.hash}
                          className={`flex items-start gap-2 text-xs p-1.5 rounded cursor-pointer transition-colors ${
                            isSelected ? 'bg-amber-100 dark:bg-amber-900/40' : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleToggleItem(item.hash)}
                            disabled={isGenerating || (selectedItems.length >= 5 && !isSelected)}
                            className="mt-0.5 h-3.5 w-3.5 rounded border-gray-300 dark:border-gray-600 text-amber-600 focus:ring-amber-500"
                          />
                          <span className="text-gray-700 dark:text-gray-200 line-clamp-1 flex-1">
                            {item.title || 'Untitled'}
                          </span>
                        </label>
                      );
                    })
                  )}
                </div>
              </div>

              {/* User Commentary */}
              <div>
                <label className="block text-xs font-medium text-amber-700 dark:text-amber-300 mb-1">
                  Your Commentary / Position *
                </label>
                <textarea
                  value={userCommentary}
                  onChange={(e) => setUserCommentary(e.target.value)}
                  placeholder="Share your thoughts, perspective, or thesis that you want to expand on..."
                  disabled={isGenerating}
                  rows={4}
                  className="w-full px-3 py-2 text-sm border border-amber-200 dark:border-amber-800/50 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-amber-500 focus:border-amber-500 resize-none"
                />
                <p className="text-[10px] text-amber-600 dark:text-amber-400 mt-1">
                  {userCommentary.length}/10 minimum characters
                </p>
              </div>

              {/* Evidence Guidance */}
              <div>
                <label className="block text-xs font-medium text-amber-700 dark:text-amber-300 mb-1">
                  Evidence Guidance (optional)
                </label>
                <textarea
                  value={evidenceGuidance}
                  onChange={(e) => setEvidenceGuidance(e.target.value)}
                  placeholder="How should the AI use the reference articles? e.g., 'Use first article to support, second to contrast'"
                  disabled={isGenerating}
                  rows={2}
                  className="w-full px-3 py-2 text-sm border border-amber-200 dark:border-amber-800/50 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-amber-500 focus:border-amber-500 resize-none"
                />
              </div>

              <button
                onClick={handleGenerate}
                disabled={isGenerating || selectedItems.length === 0 || userCommentary.length < 10}
                className="flex items-center gap-2 px-3 py-2 text-sm bg-amber-600 text-white rounded-md hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed w-full justify-center"
              >
                {isGenerating ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    Generating Content...
                  </>
                ) : (
                  <>
                    <SparklesIcon className="h-4 w-4" />
                    Generate {sectionType === 'opinion' ? 'Opinion Piece' : 'Deep Dive'}
                  </>
                )}
              </button>
            </div>
          )}

          {/* Generated Content Display */}
          {displayContent && (
            <div className="space-y-4">
              {/* Title */}
              <div>
                <label className="block text-xs font-medium text-amber-700 dark:text-amber-300 mb-1">Title</label>
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 p-3 bg-white dark:bg-gray-800 rounded-lg border border-amber-200 dark:border-amber-800/50">
                  {displayContent.title}
                </p>
              </div>

              {/* Paragraphs */}
              <div>
                <label className="block text-xs font-medium text-amber-700 dark:text-amber-300 mb-1">Content</label>
                <div className="space-y-3">
                  {displayContent.paragraphs.map((paragraph, index) => {
                    const isEditing = editingIndex === index;
                    const currentContent = editedParagraphs[index] || paragraph.content;

                    return (
                      <div
                        key={index}
                        className="p-3 bg-white dark:bg-gray-800 rounded-lg border border-amber-200 dark:border-amber-800/50"
                      >
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <span className="text-[10px] text-amber-600 dark:text-amber-400 font-medium">
                            Paragraph {index + 1}
                            {paragraph.sourcesReferenced?.length > 0 && (
                              <span className="ml-2 text-gray-500 dark:text-gray-400">
                                (refs: {paragraph.sourcesReferenced.join(', ')})
                              </span>
                            )}
                          </span>
                          {!isEditing && (
                            <button
                              onClick={() => handleEditParagraph(index)}
                              className="p-1 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                              title="Edit paragraph"
                            >
                              <PencilIcon className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>

                        {isEditing ? (
                          <div className="space-y-2">
                            <textarea
                              value={currentContent}
                              onChange={(e) =>
                                setEditedParagraphs((prev) => ({
                                  ...prev,
                                  [index]: e.target.value,
                                }))
                              }
                              rows={4}
                              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 rounded-md focus:ring-2 focus:ring-amber-500 focus:border-amber-500 resize-none"
                            />
                            <div className="flex justify-end">
                              <button
                                onClick={() => handleSaveEdit(index)}
                                className="px-2 py-1 text-xs font-medium text-white bg-amber-600 rounded hover:bg-amber-700"
                              >
                                Done Editing
                              </button>
                            </div>
                          </div>
                        ) : (
                          <p className="text-sm text-gray-700 dark:text-gray-200 whitespace-pre-wrap">
                            {currentContent}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Suggested Edits */}
              {displayContent.suggestedEdits?.length > 0 && (
                <div>
                  <label className="block text-xs font-medium text-amber-700 dark:text-amber-300 mb-1">
                    Suggested Improvements
                  </label>
                  <ul className="text-xs text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-800 rounded-lg border border-amber-200 dark:border-amber-800/50 p-3 space-y-1">
                    {displayContent.suggestedEdits.map((suggestion, index) => (
                      <li key={index} className="flex items-start gap-1">
                        <span className="text-amber-500 dark:text-amber-400">•</span>
                        {suggestion}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex items-center gap-2">
                <button
                  onClick={handleSaveContent}
                  className="flex-1 flex items-center gap-2 px-3 py-2 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 justify-center"
                >
                  Save to Issue
                </button>
                <button
                  onClick={handleClear}
                  className="flex items-center gap-2 px-3 py-2 text-sm bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  <TrashIcon className="h-4 w-4" />
                  Clear & Start Over
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
